import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
  Card,
  InputGroup,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { BsEye, BsEyeSlash, BsPerson, BsLock } from "react-icons/bs";
import {
  BsSpeedometer2,
  BsPeopleFill,
  BsCalendarCheck,
  BsClipboardData,
  BsTrophy,
  BsShieldLock,
} from "react-icons/bs";
import { ApiUrl } from "../../ApiUrl";
import ForgotPasswordModal from "./ForgotPasswordModal";

const adminDashboardPaths = {
  ADMIN: "/admin/overall-dashboard",
  TRUSTEE: "/admin/trustee-dashboard",
  PRINCIPAL: "/admin/principal-dashboard",
  SUPERADMIN: "/admin/superadmin-dashboard",
  REGISTER: "/admin/Register-dashboard",
  ACCOUNTANT: "/admin/accountant-dashboard",
  HR: "/admin/hr-dashboard",
  EXAM: "/admin/exam-dashboard",
  TEACHER: "/admin/teacher-dashboard",
  STUDENT: "/admin/student-dashboard",
  PARENT: "/admin/parent-dashboard",
  TP: "/admin/tp-dashboard",
  LIBRARIAN: "/admin/library-dashboard",
  HOSTEL: "/admin/hostel-dashboard",
  TRANSPORT: "/admin/transport-dashboard",
};

// Every role created via "Create New Role" is stored with a single shared
// usertype ("ADMIN"); the actual role (Trustee, Principal, Accountant, etc.)
// is only known from which dashboard module the admin ticked when creating it.
// This maps that module code to the matching dashboard route.
const dashboardModuleRouteMap = {
  "dashboard.superadmin_dashboard": "/admin/superadmin-dashboard",
  "dashboard.trustee_dashboard": "/admin/trustee-dashboard",
  "dashboard.principal_dashboard": "/admin/principal-dashboard",
  "dashboard.Register_dashboard": "/admin/Register-dashboard",
  "dashboard.accountant_dashboard": "/admin/accountant-dashboard",
  "dashboard.hr_dashboard": "/admin/hr-dashboard",
  "dashboard.exam_dashboard": "/admin/exam-dashboard",
  "dashboard.tp_dashboard": "/admin/tp-dashboard",
  "dashboard.teacher_dashboard": "/admin/teacher-dashboard",
  "dashboard.student_dashboard": "/admin/student-dashboard",
  "dashboard.parent_dashboard": "/admin/parent-dashboard",
};

// Checked in this order so a role granted more than one dashboard module still
// lands on its most senior/primary dashboard first.
const dashboardModulePriority = Object.keys(dashboardModuleRouteMap);

const resolveAdminDashboardPath = (accessibleModules) => {
  const modules = Array.isArray(accessibleModules) ? accessibleModules : [];
  const matchedCode = dashboardModulePriority.find((code) =>
    modules.includes(code),
  );
  return matchedCode ? dashboardModuleRouteMap[matchedCode] : null;
};

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    showPassword: false,
  });
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  useEffect(() => {
    const userRole = sessionStorage.getItem("userRole");
    if (userRole) {
      setIsLoggedIn(true);
      onLogin(userRole);
    }
    window.onpopstate = () => {
      sessionStorage.removeItem("userRole");
      setIsLoggedIn(false);
      navigate("/login");
    };
    return () => {
      window.onpopstate = null;
    };
  }, [onLogin, navigate]);

  useEffect(() => {
    if (isLoggedIn) {
      window.history.pushState(null, "", "/");
      window.onpopstate = () => {
        window.history.pushState(null, "", "/");
      };
    }
  }, [isLoggedIn]);

  // Lock page scroll while the login screen is mounted
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalMargin = document.body.style.margin;
    document.body.style.overflow = "hidden";
    document.body.style.margin = "0";
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.margin = originalMargin;
    };
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const togglePasswordVisibility = () => {
    setFormData({
      ...formData,
      showPassword: !formData.showPassword,
    });
  };

  const validateForm = () => {
    let newErrors = {};
    if (formData.username.trim() === "") {
      newErrors.username = "User Name is required";
    }
    if (formData.password.trim() === "") {
      newErrors.password = "Password is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const parseLoginResponse = async (response) => {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      return response.json();
    }

    const text = await response.text();
    return {
      message:
        text && !text.trim().startsWith("<")
          ? text
          : `Server returned ${response.status} ${response.statusText}`,
    };
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      setLoginError("");
      const response = await fetch(`${ApiUrl.apiurl}RegisterEmployee/Login/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
        }),
      });

      const data = await parseLoginResponse(response);

      if (!response.ok) {
        throw new Error(data?.message || data?.error || "Failed to log in");
      }
      const { loginId, userId, username, userRole, userTypeId, employeeType } =
        data.data;
      const normalizedRole = String(userRole || "").toUpperCase();
      const enteredUsername = String(formData.username || "").trim();
      const looksLikeStaffEmailLogin = enteredUsername.includes("@");
      const normalizedEmployeeType = String(employeeType || "")
        .trim()
        .toLowerCase();
      const isTeachingEmployee =
        looksLikeStaffEmailLogin &&
        normalizedEmployeeType.includes("teaching") &&
        !normalizedEmployeeType.includes("non teaching");
      const isNonTeachingEmployee =
        looksLikeStaffEmailLogin &&
        normalizedEmployeeType &&
        !isTeachingEmployee;

      if (isNonTeachingEmployee) {
        throw new Error(
          "You are not authorized to login to the staff panel. Please contact your administration.",
        );
      }

      const effectiveRole = normalizedRole;
      const effectiveRoleLower = effectiveRole.toLowerCase();

      sessionStorage.setItem("userId", userId);
      sessionStorage.setItem("loginId", loginId || userId);
      sessionStorage.setItem("username", username);
      sessionStorage.setItem("userRole", effectiveRoleLower);
      sessionStorage.setItem("dashboardRole", effectiveRoleLower);
      sessionStorage.setItem("userTypeId", userTypeId);

      const accessible_modules = data.data?.accessible_modules || [];
      sessionStorage.setItem(
        "accessible_modules",
        JSON.stringify(accessible_modules),
      );

      const resetAcademicSessionSelection = () => {
        localStorage.removeItem("academicSessionId");
        localStorage.removeItem("nextAcademicSessionId");
        localStorage.removeItem("orgId");
        localStorage.removeItem("branchId");
        localStorage.removeItem("dateFrom");
        sessionStorage.setItem("forceAutoAcademicSession", "true");
      };

      switch (effectiveRole) {
        case "STAFF":
        case "TEACHER":
          resetAcademicSessionSelection();
          navigate("/staff/teacher-dashboard");
          onLogin("staff");
          break;
        case "STUDENT":
          resetAcademicSessionSelection();
          navigate("/student/student-dashboard");
          onLogin("student");
          break;
        case "ADMIN": {
          const resolvedDashboardPath =
            resolveAdminDashboardPath(accessible_modules) ||
            adminDashboardPaths.ADMIN;
          navigate(resolvedDashboardPath);
          onLogin("admin");
          break;
        }
        default: {
          const dashboardPath = adminDashboardPaths[effectiveRole];
          if (!dashboardPath) {
            throw new Error(`Unknown user role: ${userRole}`);
          }

          navigate(dashboardPath);
          onLogin("admin");
          break;
        }
      }

      setIsLoggedIn(true);
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage = error.message || "Login failed";
      if (errorMessage.toLowerCase().includes("not authorized")) {
        alert(errorMessage);
      }
      setLoginError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const isValid = validateForm();
    if (isValid) {
      handleLogin();
    }
  };

  const features = [
    {
      icon: <BsSpeedometer2 />,
      label: "Smart Dashboard",
      sub: "Real-time insights & analytics",
    },
    {
      icon: <BsPeopleFill />,
      label: "Student Management",
      sub: "Manage student information",
    },
    {
      icon: <BsCalendarCheck />,
      label: "Attendance Tracking",
      sub: "Track attendance in real-time",
    },
    {
      icon: <BsClipboardData />,
      label: "Exams & Results",
      sub: "Manage exams and results",
    },
    {
      icon: <BsTrophy />,
      label: "Activities Management",
      sub: "Sports, events & more",
    },
    {
      icon: <BsShieldLock />,
      label: "Secure & Reliable",
      sub: "Your data is always safe",
    },
  ];

  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        position: "fixed",
        top: 0,
        left: 0,
        // pushed further down (30%) so more sky is trimmed off the top and
        // the "INTERNATIONAL SCHOOL" building sits right near the top edge
        backgroundImage: `url('${process.env.PUBLIC_URL}/Assets/schoolbackground.png')`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center 30%",
        backgroundColor: "#0b1830",
      }}
    >
      <Container
        fluid
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          position: "relative",
          zIndex: 1,
          padding: "16px 16px 0",
        }}
      >
        <Row className="justify-content-center">
          <Col xs={12} sm={10} md={8} lg={6} xl={5}>
            <Card
              className="border-0"
              style={{
                borderRadius: "20px",
                overflow: "hidden",
                boxShadow: "0 20px 50px rgba(0, 0, 0, 0.35)",
                background: "rgba(255, 255, 255, 0.97)",
                backdropFilter: "blur(2px)",
              }}
            >
              <Card.Body style={{ padding: "26px 32px 24px" }}>
                <div className="text-center mb-3">
                  <img
                    src={`${process.env.PUBLIC_URL}/Assets/Greenwood.png`}
                    alt="School logo"
                    style={{
                      width: "110px",
                      maxWidth: "100%",
                      height: "auto",
                      objectFit: "contain",
                      borderRadius: "10px",
                      boxShadow: "0 6px 16px rgba(0,0,0,0.14)",
                    }}
                  />
                  <h4
                    style={{
                      margin: "12px 0 2px",
                      color: "#0d3a66",
                      fontWeight: 700,
                    }}
                  >
                    Welcome to Greenwood High
                  </h4>
                  {/* <p
                    style={{ margin: 0, color: "#6b7280", fontSize: "0.85rem" }}
                  >
                    Sign in to access your dashboard
                  </p> */}
                  {/* <div
                    style={{
                      width: "50px",
                      height: "3px",
                      background: "#1d6fb6",
                      borderRadius: "2px",
                      margin: "10px auto 0",
                    }}
                  /> */}
                </div>

                {isLoggedIn ? (
                  <Alert variant="success" className="text-center mb-0">
                    Logged in successfully!
                  </Alert>
                ) : (
                  <Form onSubmit={handleSubmit}>
                    {loginError && (
                      <Alert variant="danger" className="text-center mb-3 py-2">
                        {loginError}
                      </Alert>
                    )}

                    <Form.Group className="mb-2">
                      <Form.Label className="fw-semibold text-dark mb-1">
                        Username
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text
                          style={{ background: "#fff", color: "#1d6fb6" }}
                        >
                          <BsPerson />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          name="username"
                          value={formData.username}
                          onChange={handleInputChange}
                          isInvalid={!!errors.username}
                          placeholder="Enter your username"
                          style={{
                            borderLeft: "none",
                            borderRadius: "0 8px 8px 0",
                          }}
                        />
                      </InputGroup>
                      {errors.username && (
                        <div className="text-danger small mt-1">
                          {errors.username}
                        </div>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label className="fw-semibold text-dark mb-1">
                        Password
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text
                          style={{ background: "#fff", color: "#1d6fb6" }}
                        >
                          <BsLock />
                        </InputGroup.Text>
                        <Form.Control
                          type={formData.showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          isInvalid={!!errors.password}
                          placeholder="Enter your password"
                          style={{
                            borderLeft: "none",
                            borderRadius: "0 8px 8px 0",
                          }}
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={togglePasswordVisibility}
                          style={{
                            borderLeft: "none",
                            borderRadius: "0 8px 8px 0",
                          }}
                        >
                          {formData.showPassword ? <BsEyeSlash /> : <BsEye />}
                        </Button>
                      </InputGroup>
                      {errors.password && (
                        <div className="text-danger small mt-1">
                          {errors.password}
                        </div>
                      )}
                    </Form.Group>

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Remember me"
                        id="rememberMe"
                      />
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="btn btn-link p-0"
                        style={{ fontSize: "0.85rem", color: "#1d6fb6" }}
                      >
                        Forgot Password?
                      </button>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      style={{
                        width: "100%",
                        borderRadius: "999px",
                        padding: "10px 16px",
                        fontWeight: 600,
                        border: "none",
                        background:
                          "linear-gradient(135deg, #0f4c81 0%, #1d6fb6 100%)",
                      }}
                    >
                      {loading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        "Login"
                      )}
                    </Button>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <ForgotPasswordModal
        show={showForgotPassword}
        onHide={() => setShowForgotPassword(false)}
      />

      {/* feature strip + footer, pinned to the bottom of the screen */}
      <div
        style={{ position: "relative", zIndex: 1, padding: "10px 16px 8px" }}
      >
        <Row className="g-2 justify-content-center">
          {features.map((f, i) => (
            <Col key={i} xs={4} sm={4} md={2} className="text-center">
              <div
                style={{
                  color: "#fff",
                  fontSize: "1.1rem",
                  marginBottom: "2px",
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))",
                }}
              >
                {f.icon}
              </div>
              <div
                style={{
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  lineHeight: 1.15,
                  textShadow: "0 1px 3px rgba(0,0,0,0.6)",
                }}
              >
                {f.label}
              </div>
              <div
                style={{
                  color: "rgba(255,255,255,0.85)",
                  fontSize: "0.62rem",
                  lineHeight: 1.15,
                  textShadow: "0 1px 3px rgba(0,0,0,0.6)",
                }}
              >
                {f.sub}
              </div>
            </Col>
          ))}
        </Row>
        <p
          className="text-center mb-0 mt-2"
          style={{
            color: "rgba(255,255,255,0.85)",
            fontSize: "0.7rem",
            textShadow: "0 1px 3px rgba(0,0,0,0.6)",
          }}
        >
          © {new Date().getFullYear()} International School. All Rights
          Reserved.
        </p>
      </div>
    </div>
  );
};

export default Login;
